import PushNotificationCard from "../components/PushNotificationCard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Smartphone, Bell, Info, Send, CheckCircle2 } from "lucide-react";
import { isIosSafari, isStandalone } from "../lib/push";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

/**
 * Notifications page — accessible to EVERY authenticated user (not admin-gated),
 * so anyone with an account can enable push on their own device for inbound
 * customer texts, new leads, payments, etc.
 *
 * Two rendering paths:
 *   • Native iOS (Capacitor shell) → shows the "Native push is active" card
 *     with a test button that triggers `/api/push/test`, which fans out to
 *     APNs on the backend.
 *   • Regular web browser → shows the standard PushNotificationCard for
 *     VAPID web push registration.
 */

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function isNativePlatform() {
  try {
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

function NativePushCard() {
  const [sending, setSending] = useState(false);
  const [debugLog, setDebugLog] = useState([]);

  const ensureRegistered = async (log) => {
    try {
      const { initNativePush } = await import("../lib/nativePush");
      const result = await initNativePush(undefined, (step, detail) => {
        const line = detail !== undefined ? `${step}: ${typeof detail === "string" ? detail : JSON.stringify(detail)}` : step;
        log.push(line);
        setDebugLog([...log]);
      });
      if (!result.registered) {
        log.push(`✗ Not registered — reason: ${result.reason}${result.error ? ` (${result.error})` : ""}`);
        setDebugLog([...log]);
      }
      return result;
    } catch (e) {
      log.push(`✗ initNativePush threw: ${e?.message || e}`);
      setDebugLog([...log]);
      return { registered: false };
    }
  };

  const send = async () => {
    const log = [];
    setDebugLog([]);
    setSending(true);
    try {
      log.push("→ Sending test (1st attempt)");
      setDebugLog([...log]);
      let r = await axios.post(
        `${API}/push/test`,
        { title: "Tides Hub test", body: "Push notifications are working ✓" },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
      );
      let sent = r.data?.sent ?? 0;
      log.push(`← Backend response: ${sent} device(s) sent to`);
      setDebugLog([...log]);

      if (sent === 0) {
        log.push("→ Registering this device with APNs…");
        setDebugLog([...log]);
        await ensureRegistered(log);
        log.push("→ Waiting 2.5s for token round-trip…");
        setDebugLog([...log]);
        await new Promise((res) => setTimeout(res, 2500));
        log.push("→ Sending test (2nd attempt)");
        setDebugLog([...log]);
        r = await axios.post(
          `${API}/push/test`,
          { title: "Tides Hub test", body: "Push notifications are working ✓" },
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
        );
        sent = r.data?.sent ?? 0;
        log.push(`← Backend response: ${sent} device(s) sent to`);
        setDebugLog([...log]);
      }

      if (sent > 0) toast.success(`Test sent to ${sent} device${sent === 1 ? "" : "s"}`);
      else toast.error("Still 0 devices — see the debug log below");
    } catch (err) {
      log.push(`✗ Error: ${err?.response?.data?.detail || err?.message || err}`);
      setDebugLog([...log]);
      toast.error(err?.response?.data?.detail || err?.message || "Failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card data-testid="native-push-card" className="border-emerald-200 bg-emerald-50/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-emerald-900">
          <CheckCircle2 className="w-5 h-5" /> Native push is active
        </CardTitle>
        <CardDescription className="text-emerald-800">
          You&apos;re inside the Tides Hub iOS app, which uses Apple&apos;s native push
          service (APNs). Notifications will buzz your phone even when the app is
          closed. Manage per-app permissions in iOS <b>Settings → Tides Hub → Notifications</b>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          type="button"
          onClick={send}
          disabled={sending}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="native-push-test-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          {sending ? "Sending…" : "Send test notification"}
        </Button>
        {debugLog.length > 0 && (
          <div className="mt-3 rounded-md bg-slate-900 text-slate-100 text-[11px] font-mono p-3 whitespace-pre-wrap break-all leading-snug max-h-64 overflow-auto" data-testid="native-push-debug-log">
            {debugLog.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Notifications() {
  const [native, setNative] = useState(false);
  useEffect(() => { setNative(isNativePlatform()); }, []);

  const showIosHint = !native && isIosSafari() && !isStandalone();

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5" data-testid="notifications-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Bell className="w-6 h-6 text-[#0F4C81]" /> Notifications
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Enable push notifications on this device so you never miss a customer text or lead.
          Each device (phone, tablet, laptop) subscribes independently — turn it on wherever you want to be notified.
        </p>
      </div>

      {showIosHint && (
        <Card className="border-amber-200 bg-amber-50" data-testid="notifications-ios-hint">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-amber-900 text-base">
              <Smartphone className="w-4 h-4" /> One-time iPhone / iPad setup
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-amber-900 space-y-2">
            <p><b>Step 1.</b> Tap the <b>Share</b> icon at the bottom of Safari.</p>
            <p><b>Step 2.</b> Scroll and tap <b>Add to Home Screen</b> → name it &quot;Tides Hub&quot; → Add.</p>
            <p><b>Step 3.</b> Close Safari, open the new home-screen icon, come back to this page, and flip the switch below. iOS will prompt you to allow notifications.</p>
            <p className="text-xs text-amber-800 pt-1 border-t border-amber-200">
              Apple only allows web push from apps installed on your home screen — this is a one-time setup per device.
            </p>
          </CardContent>
        </Card>
      )}

      {native ? <NativePushCard /> : <PushNotificationCard />}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Info className="w-4 h-4 text-slate-500" /> What you&apos;ll get notified about</CardTitle>
          <CardDescription>Every user on your team is notified for these events. Tap any notification to jump straight to it in the app.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-slate-700 space-y-2">
            <li className="flex items-start gap-2"><span className="text-lg leading-none">💬</span><span><b>Inbound customer texts</b> — every SMS reply to your Twilio number lands here. Tap to open the thread.</span></li>
            <li className="flex items-start gap-2"><span className="text-lg leading-none">🎯</span><span><b>New leads</b> — website form submissions, referrals, and manual contact adds.</span></li>
            <li className="flex items-start gap-2"><span className="text-lg leading-none">✍️</span><span><b>Signed quotes</b> — the moment a customer counter-signs.</span></li>
            <li className="flex items-start gap-2"><span className="text-lg leading-none">💰</span><span><b>Payments &amp; full pay-offs</b> — deposit, progress, and final invoice hits.</span></li>
            <li className="flex items-start gap-2"><span className="text-lg leading-none">📧</span><span><b>Email engagement</b> — invoice views, bounces, and customer replies.</span></li>
            <li className="flex items-start gap-2"><span className="text-lg leading-none">✅</span><span><b>Tasks assigned to you</b> — direct assignments only, not team-wide items.</span></li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
